/**
 * @fileoverview Shared task filter + group/sort bar (MOR-696 Stage 2)
 *
 * One filter surface reused by the kanban board, the list/table view, and the
 * workspace "My tasks" inbox. Holds the current `TaskFilterState` (status /
 * assignee / label / priority / category / overdue / today / due-date range /
 * subtask visibility / search) plus the optional group-by + sort-by
 * selectors. `applyTaskFilters` is the pure predicate the hosting routes run
 * over the `CalendarEventRow[]` stream, so the same filter logic backs every
 * surface and the saved-view restore path.
 */

import { useState } from "react";
import { format, parseISO } from "date-fns";
import {
  AlertCircle,
  ArrowUpDown,
  BarChart3,
  Briefcase,
  CalendarClock,
  CalendarRange,
  CircleDashed,
  ListFilter,
  Plus,
  Search,
  Shapes,
  SlidersHorizontal,
  Tag,
  User,
  X,
  type LucideIcon,
} from "lucide-react";

import {
  CATEGORY_META,
  TASK_CATEGORY_ORDER,
  isKeyDateCategory,
} from "@/calendar/categoryMeta";
import { TASK_PRIORITIES } from "@/calendar/task-priority";
import type { JobStatus } from "@cvx/jobs/schema";
import type {
  TaskDueRange,
  TaskDueRangePreset,
  TaskViewGroupBy,
  TaskViewSortBy,
} from "@cvx/tasks/taskViews/schema";
import { emptyFilters, type TaskFilterState } from "./task-filter-bar.helpers";
import { JOB_STATUS_OPTIONS } from "@/lib/job-statuses";
import {
  MultiSearchableSelect,
  type MultiSearchableSelectOption,
} from "@/ui/composed/inputs/multi-searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Toggle } from "@/ui/primitives/toggle";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";

const CATEGORY_OPTIONS: MultiSearchableSelectOption[] = TASK_CATEGORY_ORDER.map(
  (c) => ({
    value: c,
    label: CATEGORY_META[c].label,
  }),
);

const PRIORITY_OPTIONS: MultiSearchableSelectOption[] = TASK_PRIORITIES.map(
  (p) => ({ value: String(p.value), label: p.label }),
);

const JOB_STATUS_FILTER_OPTIONS: MultiSearchableSelectOption[] =
  JOB_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

function isJobStatus(value: string): value is JobStatus {
  return JOB_STATUS_OPTIONS.some((o) => o.value === value);
}

const GROUP_BY_OPTIONS: Array<{ value: TaskViewGroupBy; label: string }> = [
  { value: "status", label: "Status" },
  { value: "assignee", label: "Assignee" },
  { value: "priority", label: "Priority" },
  { value: "label", label: "Label" },
  { value: "job", label: "Job" },
  { value: "none", label: "None" },
];

function isTaskViewGroupBy(value: string): value is TaskViewGroupBy {
  return GROUP_BY_OPTIONS.some((o) => o.value === value);
}

const SORT_BY_OPTIONS: Array<{ value: TaskViewSortBy; label: string }> = [
  { value: "date", label: "Due date" },
  { value: "plannedDate", label: "Planned date" },
  { value: "priority", label: "Priority" },
  { value: "created", label: "Created" },
  { value: "title", label: "Title" },
  { value: "manual", label: "Manual" },
];

function isTaskViewSortBy(value: string): value is TaskViewSortBy {
  return SORT_BY_OPTIONS.some((o) => o.value === value);
}

// ---------------------------------------------------------------------------
// Filter dimensions (Linear-style)
// ---------------------------------------------------------------------------

/** Stable keys for each filterable dimension. */
type FilterKey =
  | "status"
  | "assignee"
  | "priority"
  | "label"
  | "category"
  | "jobStatus"
  | "overdue"
  | "today"
  | "dueRange";

/** A multi-value dimension (status / assignee / priority / label / category). */
interface MultiDim {
  key: FilterKey;
  kind: "multi";
  label: string;
  icon: LucideIcon;
  /** Tailwind text-colour class for the dimension's icon. */
  iconClass: string;
  options: MultiSearchableSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  searchPlaceholder: string;
}

/** A boolean toggle dimension (overdue / today). Present ⇒ true. */
interface BoolDim {
  key: FilterKey;
  kind: "bool";
  label: string;
  icon: LucideIcon;
  /** Tailwind text-colour class for the dimension's icon. */
  iconClass: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

/** The due-date range dimension — a preset (or custom dates) + overdue flag. */
interface DueRangeDim {
  key: FilterKey;
  kind: "dueRange";
  label: string;
  icon: LucideIcon;
  /** Tailwind text-colour class for the dimension's icon. */
  iconClass: string;
  value: TaskDueRange | null;
  onChange: (value: TaskDueRange | null) => void;
}

type Dim = MultiDim | BoolDim | DueRangeDim;

const dimHasValue = (dim: Dim): boolean =>
  dim.kind === "multi"
    ? dim.values.length > 0
    : dim.kind === "dueRange"
      ? dim.value !== null
      : dim.value;

const DUE_RANGE_PRESET_OPTIONS: Array<{
  value: TaskDueRangePreset;
  label: string;
}> = [
  { value: "thisWeek", label: "This week" },
  { value: "thisFortnight", label: "This fortnight" },
  { value: "thisMonth", label: "This month" },
  { value: "thisQuarter", label: "This quarter" },
  { value: "custom", label: "Custom range" },
];

function isTaskDueRangePreset(value: string): value is TaskDueRangePreset {
  return DUE_RANGE_PRESET_OPTIONS.some((o) => o.value === value);
}

/** Human label for the active due-range chip ("This week + overdue"). */
function dueRangeLabel(range: TaskDueRange): string {
  const day = (isoDate: string | undefined) =>
    isoDate ? format(parseISO(isoDate), "d MMM") : "…";

  const base =
    range.preset === "custom" && (range.from || range.to)
      ? `${day(range.from)} – ${day(range.to)}`
      : (DUE_RANGE_PRESET_OPTIONS.find((o) => o.value === range.preset)
          ?.label ?? "Custom range");

  return range.includeOverdue ? `${base} + overdue` : base;
}

/** Popover editor for the due-range chip: preset, custom dates, + overdue. */
function DueRangeEditor({
  value,
  onChange,
  autoOpen,
}: {
  value: TaskDueRange;
  onChange: (value: TaskDueRange) => void;
  autoOpen: boolean;
}) {
  const setPreset = (preset: TaskDueRangePreset) =>
    onChange({
      preset,
      // Fixed dates only make sense on the custom preset.
      ...(preset === "custom" ? { from: value.from, to: value.to } : {}),
      ...(value.includeOverdue ? { includeOverdue: true } : {}),
    });

  return (
    <Popover defaultOpen={autoOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center pl-0 pr-2 font-medium hover:bg-muted/50"
        >
          {dueRangeLabel(value)}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3 p-3" align="start">
        <Select
          value={value.preset}
          onValueChange={(v) => {
            if (isTaskDueRangePreset(v)) setPreset(v);
          }}
        >
          <SelectTrigger className="h-8 w-full text-xs">
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            {DUE_RANGE_PRESET_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {value.preset === "custom" && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={value.from ?? ""}
              onChange={(e) =>
                onChange({ ...value, from: e.target.value || undefined })
              }
              className="h-8 text-xs"
              aria-label="Due from"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={value.to ?? ""}
              onChange={(e) =>
                onChange({ ...value, to: e.target.value || undefined })
              }
              className="h-8 text-xs"
              aria-label="Due to"
            />
          </div>
        )}

        <div className="flex items-start gap-2">
          <Checkbox
            id="due-range-include-overdue"
            checked={value.includeOverdue ?? false}
            onCheckedChange={(checked) =>
              onChange({ ...value, includeOverdue: checked === true })
            }
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label
              htmlFor="due-range-include-overdue"
              className="cursor-pointer text-xs font-medium leading-none"
            >
              Include overdue
            </Label>
            <p className="text-xs text-muted-foreground">
              Also show overdue tasks from before this period.
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The chip a single active filter renders as, above the table. */
function FilterChip({
  dim,
  autoOpen,
  onRemove,
}: {
  dim: Dim;
  autoOpen: boolean;
  onRemove: () => void;
}) {
  const Icon = dim.icon;

  if (dim.kind === "bool") {
    return (
      <div className="inline-flex h-7 items-center gap-1.5 rounded-md border bg-background pl-2 pr-1 text-xs">
        <Icon className={cn("size-3.5 shrink-0", dim.iconClass)} />
        <span className="font-medium">{dim.label}</span>
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 flex size-4 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Remove ${dim.label} filter`}
        >
          <X className="size-3" />
        </button>
      </div>
    );
  }

  if (dim.kind === "dueRange") {
    // Activation always seeds a value, so a rendered chip is never empty.
    if (!dim.value) return null;
    return (
      <div className="inline-flex h-7 items-stretch overflow-hidden rounded-md border bg-background text-xs">
        <span className="flex items-center gap-1.5 border-r bg-muted/40 px-2 font-medium">
          <Icon className={cn("size-3.5 shrink-0", dim.iconClass)} />
          {dim.label}
        </span>
        <span className="flex items-center pl-2 pr-1 text-xs text-muted-foreground/70">
          is
        </span>
        <DueRangeEditor
          value={dim.value}
          onChange={dim.onChange}
          autoOpen={autoOpen}
        />
        <button
          type="button"
          onClick={onRemove}
          className="flex items-center border-l px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Remove ${dim.label} filter`}
        >
          <X className="size-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex h-7 items-stretch overflow-hidden rounded-md border bg-background text-xs">
      <span className="flex items-center gap-1.5 border-r bg-muted/40 px-2 font-medium">
        <Icon className={cn("size-3.5 shrink-0", dim.iconClass)} />
        {dim.label}
      </span>
      <span className="flex items-center pl-2 pr-1 text-xs text-muted-foreground/70">
        is
      </span>
      <MultiSearchableSelect
        value={dim.values}
        onValueChange={dim.onChange}
        options={dim.options}
        placeholder="any"
        searchPlaceholder={dim.searchPlaceholder}
        defaultOpen={autoOpen}
        className="h-full gap-1 rounded-none border-0 bg-transparent pl-0 pr-2 hover:bg-muted/50 focus:ring-0"
        contentClassName="w-[240px]"
      />
      <button
        type="button"
        onClick={onRemove}
        className="flex items-center border-l px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={`Remove ${dim.label} filter`}
      >
        <X className="size-3" />
      </button>
    </div>
  );
}

/** Dropdown that lists dimensions not yet active and adds them on select. */
function AddFilterMenu({
  available,
  onActivate,
  trigger,
}: {
  available: Dim[];
  onActivate: (dim: Dim) => void;
  trigger: React.ReactNode;
}) {
  if (available.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs">Add filter…</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {available.map((dim) => {
          const Icon = dim.icon;
          return (
            <DropdownMenuItem
              key={dim.key}
              onSelect={() => onActivate(dim)}
              className="gap-2 text-xs"
            >
              <Icon className={cn("size-3.5", dim.iconClass)} />
              {dim.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TaskFilterBar({
  filters,
  onChange,
  statusOptions,
  assigneeOptions,
  labelOptions,
  groupBy,
  onGroupByChange,
  subGroupBy,
  onSubGroupByChange,
  sortBy,
  onSortByChange,
  hideEvents,
  onHideEventsChange,
  showJobStatusFilter,
  viewSwitcher,
  trailing,
}: {
  filters: TaskFilterState;
  onChange: (next: TaskFilterState) => void;
  statusOptions: MultiSearchableSelectOption[];
  assigneeOptions: MultiSearchableSelectOption[];
  labelOptions: MultiSearchableSelectOption[];
  /**
   * Offer the job-status dimension (cross-job surfaces only — rows on a
   * single job's board all share one status, so the chip would be noise).
   */
  showJobStatusFilter?: boolean;
  /** When provided, render the group-by selector (board hides it). */
  groupBy?: TaskViewGroupBy;
  onGroupByChange?: (value: TaskViewGroupBy) => void;
  /** When provided, render the secondary group-by (swimlane) selector. */
  subGroupBy?: TaskViewGroupBy;
  onSubGroupByChange?: (value: TaskViewGroupBy) => void;
  sortBy?: TaskViewSortBy;
  onSortByChange?: (value: TaskViewSortBy) => void;
  /**
   * "Hide events" view toggle — when provided, renders a removable toggle that
   * hides calendar-only categories (appointment anchors, meetings, milestones,
   * investigation windows) from the board/list.
   */
  hideEvents?: boolean;
  onHideEventsChange?: (value: boolean) => void;
  /** Saved-view switcher, rendered inline beside the Hide events toggle. */
  viewSwitcher?: React.ReactNode;
  /** Slot for the right-aligned actions (New task / Columns / view toggle). */
  trailing?: React.ReactNode;
}) {
  const set = (patch: Partial<TaskFilterState>) =>
    onChange({ ...filters, ...patch });

  // Dimensions the user explicitly added but hasn't given a value yet — kept
  // visible as empty chips (Linear behaviour). Dimensions that already carry a
  // value show as chips automatically (e.g. the "My Tasks" assignee preset).
  const [manualKeys, setManualKeys] = useState<Set<FilterKey>>(new Set());
  // The just-added multi dimension, so its value popover opens immediately.
  const [autoOpenKey, setAutoOpenKey] = useState<FilterKey | null>(null);

  const dims: Dim[] = [
    {
      key: "status",
      kind: "multi",
      label: "Status",
      icon: CircleDashed,
      iconClass: "text-sky-500",
      options: statusOptions,
      values: filters.statusIds,
      onChange: (v) => set({ statusIds: v }),
      searchPlaceholder: "Search statuses…",
    },
    {
      key: "assignee",
      kind: "multi",
      label: "Assignee",
      icon: User,
      iconClass: "text-violet-500",
      options: assigneeOptions,
      values: filters.assigneeUserIds,
      onChange: (v) => set({ assigneeUserIds: v }),
      searchPlaceholder: "Search members…",
    },
    {
      key: "priority",
      kind: "multi",
      label: "Priority",
      icon: BarChart3,
      iconClass: "text-amber-500",
      options: PRIORITY_OPTIONS,
      values: filters.priorities.map(String),
      onChange: (v) => set({ priorities: v.map(Number) }),
      searchPlaceholder: "Search priorities…",
    },
    {
      key: "label",
      kind: "multi",
      label: "Label",
      icon: Tag,
      iconClass: "text-pink-500",
      options: labelOptions,
      values: filters.labelIds,
      onChange: (v) => set({ labelIds: v }),
      searchPlaceholder: "Search labels…",
    },
    {
      key: "category",
      kind: "multi",
      label: "Category",
      icon: Shapes,
      iconClass: "text-emerald-500",
      options: CATEGORY_OPTIONS,
      values: filters.categories,
      onChange: (v) => set({ categories: v.filter(isKeyDateCategory) }),
      searchPlaceholder: "Search categories…",
    },
    ...(showJobStatusFilter
      ? [
          {
            key: "jobStatus",
            kind: "multi",
            label: "Job status",
            icon: Briefcase,
            iconClass: "text-slate-500",
            options: JOB_STATUS_FILTER_OPTIONS,
            values: filters.jobStatuses,
            onChange: (v: string[]) =>
              set({ jobStatuses: v.filter(isJobStatus) }),
            searchPlaceholder: "Search job statuses…",
          } satisfies MultiDim,
        ]
      : []),
    {
      key: "dueRange",
      kind: "dueRange",
      label: "Due date",
      icon: CalendarRange,
      iconClass: "text-teal-500",
      value: filters.dueRange,
      onChange: (v) => set({ dueRange: v }),
    },
    {
      key: "overdue",
      kind: "bool",
      label: "Overdue",
      icon: AlertCircle,
      iconClass: "text-red-500",
      value: filters.overdueOnly,
      onChange: (v) => set({ overdueOnly: v }),
    },
    {
      key: "today",
      kind: "bool",
      label: "Today",
      icon: CalendarClock,
      iconClass: "text-blue-500",
      value: filters.today,
      onChange: (v) => set({ today: v }),
    },
  ];

  const activeDims = dims.filter(
    (d) => dimHasValue(d) || manualKeys.has(d.key),
  );
  const availableDims = dims.filter(
    (d) => !(dimHasValue(d) || manualKeys.has(d.key)),
  );

  const activate = (dim: Dim) => {
    if (dim.kind === "bool") {
      dim.onChange(true);
      return;
    }
    if (dim.kind === "dueRange") {
      // Seed with the most common preset so the chip filters immediately;
      // the auto-opened popover lets the user refine it.
      dim.onChange({ preset: "thisWeek" });
      setAutoOpenKey(dim.key);
      return;
    }
    setManualKeys((prev) => new Set(prev).add(dim.key));
    setAutoOpenKey(dim.key);
  };

  const removeDim = (dim: Dim) => {
    if (dim.kind === "bool") dim.onChange(false);
    else if (dim.kind === "dueRange") dim.onChange(null);
    else dim.onChange([]);
    setManualKeys((prev) => {
      const next = new Set(prev);
      next.delete(dim.key);
      return next;
    });
    if (autoOpenKey === dim.key) setAutoOpenKey(null);
  };

  const clearAll = () => {
    // "Clear" clears the filter chips; the hide-subtasks toolbar toggle is a
    // view preference (like Hide events) and survives.
    onChange({ ...emptyFilters(), hideSubtasks: filters.hideSubtasks });
    setManualKeys(new Set());
    setAutoOpenKey(null);
  };

  const hasActive = activeDims.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Controls row: search, add-filter, grouping/sorting, trailing. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => set({ search: e.target.value })}
            placeholder="Search tasks…"
            className="h-9 w-48 pl-7"
          />
        </div>

        <AddFilterMenu
          available={availableDims}
          onActivate={activate}
          trigger={
            <Button variant="outline" size="sm" className="h-9 gap-1.5">
              <ListFilter className="size-3.5" />
              Filter
            </Button>
          }
        />

        {groupBy !== undefined && onGroupByChange && (
          <Select
            value={groupBy}
            onValueChange={(v) => {
              if (isTaskViewGroupBy(v)) onGroupByChange(v);
            }}
          >
            <SelectTrigger className="h-9 w-auto min-w-[140px] gap-2">
              <SlidersHorizontal className="size-3.5 shrink-0" />
              <SelectValue placeholder="Group" />
            </SelectTrigger>
            <SelectContent>
              {GROUP_BY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  Group: {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {subGroupBy !== undefined &&
          onSubGroupByChange &&
          groupBy !== undefined &&
          groupBy !== "none" && (
            <Select
              value={subGroupBy}
              onValueChange={(v) => {
                if (isTaskViewGroupBy(v)) onSubGroupByChange(v);
              }}
            >
              <SelectTrigger className="h-9 w-auto min-w-[150px] gap-2">
                <SlidersHorizontal className="size-3.5 shrink-0" />
                <SelectValue placeholder="Then by" />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BY_OPTIONS.filter((o) => o.value !== groupBy).map(
                  (o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.value === "none"
                        ? "No sub-group"
                        : `Then by: ${o.label}`}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          )}

        {sortBy !== undefined && onSortByChange && (
          <Select
            value={sortBy}
            onValueChange={(v) => {
              if (isTaskViewSortBy(v)) onSortByChange(v);
            }}
          >
            <SelectTrigger className="h-9 w-auto min-w-[140px] gap-2">
              <ArrowUpDown className="size-3.5 shrink-0" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_BY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  Sort: {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {onHideEventsChange && (
          <Toggle
            size="sm"
            variant="outline"
            pressed={hideEvents ?? false}
            onPressedChange={(pressed) => onHideEventsChange(pressed)}
            className="h-9 data-[state=on]:bg-amber-100 data-[state=on]:text-amber-800 dark:data-[state=on]:bg-amber-950 dark:data-[state=on]:text-amber-300"
          >
            Hide events
          </Toggle>
        )}

        {/* Rides `filters` (not local state) so saved views persist it. */}
        <Toggle
          size="sm"
          variant="outline"
          pressed={filters.hideSubtasks}
          onPressedChange={(pressed) => set({ hideSubtasks: pressed })}
          className="h-9 data-[state=on]:bg-amber-100 data-[state=on]:text-amber-800 dark:data-[state=on]:bg-amber-950 dark:data-[state=on]:text-amber-300"
        >
          Hide subtasks
        </Toggle>

        {viewSwitcher}

        {trailing}
      </div>

      {/* Active filter chips — above the table, each removable, with a "+"
          to add more (Linear pattern). */}
      {hasActive && (
        <div className="flex flex-wrap items-center gap-2">
          {activeDims.map((dim) => (
            <FilterChip
              key={dim.key}
              dim={dim}
              autoOpen={autoOpenKey === dim.key}
              onRemove={() => removeDim(dim)}
            />
          ))}

          <AddFilterMenu
            available={availableDims}
            onActivate={activate}
            trigger={
              <button
                type="button"
                className={cn(
                  "flex size-7 items-center justify-center rounded-md border border-dashed text-muted-foreground",
                  "transition-colors hover:bg-muted/50 hover:text-foreground",
                )}
                aria-label="Add filter"
              >
                <Plus className="size-3.5" />
              </button>
            }
          />

          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={clearAll}
          >
            <X className="size-3.5" />
            Clear
          </Button>
        </div>
      )}
    </div>
  );
}
